import { useCallback, useEffect, useState } from 'react'
import {
  ControlMode,
  IMultiMotorTelemetryMessage,
  IReadInputConfigResponse,
  IRealtimeDataMessage,
  IVariableTelemetryMessage,
  PacketId,
  SmFwBuildType,
  deserialize,
  inputReportID,
  serializeGetBuildInfoRequest,
  serializeHandshakeRequest,
  serializeReadControlModeRequest,
  serializeReadInputConfigRequest
} from '../utils/common-types'
import { hidRequest } from '../connections/hid'
import { packetThreadPoolApi } from '../utils/packet-workers'
import { deviceId, getFlashInfo, FlashInfo, readFlashMemory } from '../connections/controller'
import { SubActuatorManager, SubActuatorStep } from '../connections/pscb'
import { _updateConnectedDevice } from './useThrottleConfig' // See comment on that function for when it must be called.
import { CStore, CStoreError, CSTORE_SIZE, TagsToml, CodesToml } from 'cstore/cstore'
import { TelemIdxType, TelemToml, isFieldTypeValid } from 'cstore/telem'
import { tags, codes, telem } from 'cstore/toml/toml'
import { AccountKeyPair, Handshaker, KeepAliveTimer } from '../connections/auth/handshake'
import * as Sentry from '@sentry/react'
import { DEVICE_NAME_TAG, FIRMWARE_VERSION_TAG } from 'setup/sentry'

export const SALIENT_VENDOR_ID = 0x35ff
export const HID_WARMUP_PERIOD = 500 // ms

// Lower timeout than default for the following because older f/w will never reply
const READ_CONTROL_MODE_CMD_TIMEOUT_MS = 300
const BUILD_INFO_TIMEOUT_MS = 50

export type FwVersion = [number, number, number]
export const MIN_FW_VERSION: FwVersion = [1, 0, 0]

export type DeviceBuildInfo = {
  year: number
  month: number
  day: number
  increment: number
  buildType: SmFwBuildType
  dirty: boolean
  gitCommitSHA1: number
}

export type Device =
  | {
      initialized: false
      handle: HIDDevice
    }
  | {
      attached: boolean
      initialized: true
      handle: HIDDevice
      mcuSerialNum: string
      fwVersion: FwVersion
      inputConfig: IReadInputConfigResponse // ToDo: users of this should instead use cstore
      controlMode: ControlMode // ToDo: users of this should instead use cstore
      needToUpdate: boolean
      buildInfo: DeviceBuildInfo // Stores firmware metadata of the device related to the build
      flashInfo?: FlashInfo
      // It is guaranteed that exactly one of the following two will be defined.
      cstore?: CStore
      cstoreErr: CStoreError | string | undefined // The string case is for getFlashInfo or flash read errors (before CStore constructor)
      subActuatorManager?: SubActuatorManager
      // If handshaker is undefined, it means there's no account keypair
      handshaker?: Handshaker
      keepAliveTimer?: KeepAliveTimer
    }

// check the HID is a match, or the actual serial of the device is the same
const isSamePhysicalDevice = (connectedDevice: Device, previousDevice: Device): boolean => {
  return (
    connectedDevice.handle === previousDevice.handle ||
    (previousDevice.initialized &&
      connectedDevice.initialized &&
      connectedDevice.mcuSerialNum === previousDevice.mcuSerialNum)
  )
}

let _devices: Device[] | undefined
let _connectedDeviceHandle: HIDDevice | undefined
const _listeners: (() => void)[] = []
const _notify = () => _listeners.forEach((l) => l())

let _keypair: AccountKeyPair | undefined

const onInputReport = (event: HIDInputReportEvent) => {
  const id = inputReportID(event.data)
  const packet = deserialize(event.data)
  switch (id) {
    case PacketId.VARIABLE_TELEMETRY: {
      const { telemId, telemBytestream } = packet as IVariableTelemetryMessage
      // The "as Uint8Array" on next line is safe because of internals of
      // deserializeVariableTelemetryMessage() (which the above call to
      // deserialize() calls).
      packetThreadPoolApi.handleVariableTelemetry(
        event.timeStamp,
        telemId,
        telemBytestream as Uint8Array
      ) // spawn async
      break
    }
    case PacketId.REALTIME_DATA: {
      packetThreadPoolApi.handlePackets([
        {
          id,
          packet: packet as IRealtimeDataMessage,
          timestampMs: event.timeStamp
        }
      ])
      break
    }
    case PacketId.MULTI_MOTOR_TELEMETRY: {
      packetThreadPoolApi.handleMultiMotorTelemetry(
        event.timeStamp,
        packet as IMultiMotorTelemetryMessage
      ) // spawn async
      break
    }
  }
}

const _forceRenderDevices = () => {
  if (_devices !== undefined) _devices = [..._devices]
  _notify()
}

const _refresh = async () => {
  if (!navigator['hid']) {
    return
  }

  // If there are new devices returned by the browser WebHID API we haven't seen before, save them with initialized=false.
  let _previousDevices: Device[] | undefined = _devices
  _devices = (await navigator.hid.getDevices())
    .filter((handle) => handle.vendorId === SALIENT_VENDOR_ID)
    .map((handle) => {
      return (
        _previousDevices?.find((d) => d.handle === handle) || {
          initialized: false,
          handle
        }
      )
    })

  // 1) Open device if not already open.
  // 2) Do handshake with device and load its cstore region.
  // 3) Load the variable telemetry map (if the necessary bitfields are present in cstore).
  // 4) If user has a keypair for auth handshakes, start sending KeepAlive packets every 3 seconds.
  // 5) Update _devices array with collected device information; now all are initialized.
  for (let i = 0; i < _devices.length; i++) {
    const device: Device = _devices[i]
    // this will skip unattached devices as well, which is what we want since we want to preserve their state until they reconnect or the user manually removes them from the UI
    if (device.initialized) continue
    let needToUpdate = false

    try {
      await device.handle.open()
    } catch (e) {
      console.warn('_refresh, open device fail:', e)
    }
    const handshake = await hidRequest(
      device.handle,
      PacketId.HANDSHAKE,
      serializeHandshakeRequest({})
    )
    const fwVersion: FwVersion = [
      handshake.versionMajor,
      handshake.versionMinor,
      handshake.versionBuild
    ]
    const inputConfig = await hidRequest(
      device.handle,
      PacketId.READ_INPUT_CONFIG,
      serializeReadInputConfigRequest({})
    )

    const { controlMode } = await hidRequest(
      device.handle,
      PacketId.READ_CONTROL_MODE,
      serializeReadControlModeRequest({}),
      READ_CONTROL_MODE_CMD_TIMEOUT_MS
    )

    const mcuSerialNum = deviceId(handshake)
    const gitCommitSHA1 = handshake.gitHash

    let buildInfoResponse = await hidRequest(
      device.handle,
      PacketId.GET_BUILD_INFO,
      serializeGetBuildInfoRequest({}),
      BUILD_INFO_TIMEOUT_MS
    )
    const buildInfo = {
      year: buildInfoResponse.year,
      month: buildInfoResponse.month,
      day: buildInfoResponse.day,
      increment: buildInfoResponse.increment,
      buildType: buildInfoResponse.buildType,
      dirty: buildInfoResponse.dirty === 1,
      gitCommitSHA1
    }

    let flashInfo
    let cstore
    let cstoreErr: CStoreError | string | undefined
    try {
      const result = await loadCStore(device.handle)
      flashInfo = result.flashInfo
      cstore = result.cstore
    } catch (e: unknown) {
      if (typeof e === 'string') {
        cstoreErr = e
      } else if (e instanceof CStoreError) {
        cstoreErr = e
      } else {
        cstoreErr = `Unexpected error from loadCStore: ${e}` // should not happen
        console.error(cstoreErr)
      }
    }

    if (cstore) await loadTelemetryMap(cstore)

    // The callback is called whenever the state changes. It passes
    // disposition information to the packet workers as well as triggering
    // a re-render.
    let subActuatorManager
    if (cstore && isPSCB(cstore)) {
      subActuatorManager = new SubActuatorManager(device.handle, (state) => {
        _forceRenderDevices()
        if (state?.step === SubActuatorStep.OK) {
          packetThreadPoolApi.setActuatorDispositions(state.actuators) // spawn async
        }
      })
      subActuatorManager.run()
    }

    let handshaker
    let keepAliveTimer
    if (_keypair !== undefined) {
      keepAliveTimer = new KeepAliveTimer(device.handle, _triggerHandshake)
      handshaker = new Handshaker(_keypair, keepAliveTimer, _forceRenderDevices)
      handshaker.run(device.handle) // spawn async
    }

    _devices[i] = {
      initialized: true,
      attached: true,
      handle: device.handle,
      mcuSerialNum,
      fwVersion,
      inputConfig,
      controlMode,
      needToUpdate,
      buildInfo,
      flashInfo,
      cstore,
      cstoreErr,
      subActuatorManager,
      handshaker,
      keepAliveTimer
    }

    if (device.handle === _connectedDeviceHandle) {
      _updateConnectedDevice(_devices[i])
    }
  }

  // keep old attached devices if they exist, but mark them as no longer attached
  const detachedDevices: Device[] | undefined = _previousDevices
    ?.filter(
      // remove devices that are still connected according to the browser API, even if they don't match by handle (e.g. due to replug or WebHID quirkiness), since we want to preserve the connected state in those cases.
      (previousDevice) =>
        !_devices?.some((attachedDevice) => isSamePhysicalDevice(attachedDevice, previousDevice))
    )
    .map((d) => ({ ...d, attached: false }))

  _devices = _devices.concat(detachedDevices || [])
  _notify()
}
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

// Returns true if the device is a PSCB (no IP_* tags present in cstore).
const isPSCB = (cstore: CStore): boolean => {
  for (const tag of cstore.storedTags) {
    const def = tag.getTagDefinition(cstore.allTags)
    if (def?.name.startsWith('IP_')) return false
  }
  return true
}

// This must only throw a CStoreError (or subclass) or a string
const loadCStore = async (handle: HIDDevice): Promise<{ flashInfo: FlashInfo; cstore: CStore }> => {
  const flashInfo = await getFlashInfo(handle)
  const offset = flashInfo.cstoreOffset
  if (offset === undefined) throw 'No cstore offset (firmware too old?)'
  if (offset < 0 || offset % 4 !== 0) {
    // Avoid a PermanentFailure being thrown below; this is extremely unlikely however
    throw `Invalid flash memory start offset: ${offset}`
  }
  const cstoreBin = await readFlashMemory(handle, offset, CSTORE_SIZE)
  let cstore: CStore

  try {
    cstore = new CStore(tags as TagsToml, codes as CodesToml, cstoreBin)
    return { flashInfo, cstore }
  } catch (_e) {
    // Retry once after a short delay
    await delay(200)

    try {
      const cstoreBinRetry = await readFlashMemory(handle, offset, CSTORE_SIZE)
      console.warn('Retrying CStore load after failure...')
      cstore = new CStore(tags as TagsToml, codes as CodesToml, cstoreBinRetry)
      return { flashInfo, cstore }
    } catch (retryErr) {
      // Throw a clear error if retry fails
      throw new CStoreError(`Failed to load CStore after retry: ${retryErr}`)
    }
  }
}

const telemBitfieldsAndOffsets: [string, number][] = [
  ['Telem_Bitfield0', 0],
  ['Telem_Bitfield1', 32],
  ['Telem_Bitfield2', 64],
  ['Telem_Bitfield3', 96]
]

const loadTelemetryMap = async (cstore: CStore) => {
  let telemSet: { fieldName: string; fieldType: TelemIdxType }[] = []
  let unhandledTags = telemBitfieldsAndOffsets.length
  for (const [tagName, bitOffset] of telemBitfieldsAndOffsets) {
    let bitfield: number | undefined
    for (let i = 0; i < cstore.storedTags.length; i++) {
      const tag = cstore.storedTags[i]
      const def = tag.getTagDefinition(cstore.allTags)
      if (def && def.name === tagName) {
        bitfield = tag.getData() as number
      }
    }
    if (bitfield === undefined) continue
    unhandledTags--
    for (let shift = 0; shift < 32; shift++) {
      if (bitfield & (1 << shift)) {
        const idx = bitOffset + shift // Bit position in the full telemetry bitfield (128 bits at present).
        const entry = (telem as TelemToml).telemetry.definitions[idx]
        if (!entry) return
        const { name: fieldName, type: fieldType } = entry
        if (isFieldTypeValid(fieldType)) {
          telemSet.push({ fieldName, fieldType })
        } else {
          console.warn('Telemetry definition has unexpected field type', entry)
        }
      }
    }
  }
  if (unhandledTags) {
    // We don't have all parts of the telemetry bitfield
    console.warn('Variable telemetry unsupported?', unhandledTags)
    return
  }
  await packetThreadPoolApi.loadTelemMap(telemSet)
}

const _connect = (device: Device) => {
  _connectedDeviceHandle = device.handle
  device.handle.addEventListener('inputreport', onInputReport)
  const connectedDevice = _devices?.find((d) => d.handle === _connectedDeviceHandle)
  if (connectedDevice?.initialized) {
    _updateConnectedDevice(connectedDevice)
  }
  _notify()
}

const _disconnect = () => {
  packetThreadPoolApi.setActuatorDispositions({}) // spawn async

  _connectedDeviceHandle?.removeEventListener('inputreport', onInputReport)
  if (_connectedDeviceHandle) {
    const connectedDevice = _devices?.find((d) => d.handle === _connectedDeviceHandle)
    if (connectedDevice?.initialized) {
      connectedDevice?.keepAliveTimer?.stop()
    }
  }
  _connectedDeviceHandle = undefined
  _updateConnectedDevice(undefined)
  _notify()
}

const _triggerHandshake = (handle: HIDDevice) => {
  const device = _devices?.find((d) => d.handle === handle)
  if (device?.initialized && isDefined(_keypair)) {
    // TODO: check if there is an existing handshaker on device in an in-progress status???
    const handshaker = new Handshaker(_keypair, device.keepAliveTimer, _forceRenderDevices)
    handshaker.run(device.handle) // spawn async
    device.handshaker = handshaker
  }
}

if (navigator['hid']) {
  navigator.hid.onconnect = () => setTimeout(_refresh, HID_WARMUP_PERIOD)
}

const _pairDevice = async () => {
  const devices = await navigator.hid.requestDevice({
    filters: [{ vendorId: SALIENT_VENDOR_ID }]
  })

  if (devices[0] !== undefined) {
    await _refresh()
  }

  return devices[0]
}

export const useDevices = () => {
  const [devices, setDevices] = useState<typeof _devices>(_devices)
  const [connectedDeviceHandle, setConnectedDeviceHandle] =
    useState<typeof _connectedDeviceHandle>(_connectedDeviceHandle)
  const [triggerDisconnectExport, setTriggerDisconnectExport] = useState(false)
  const connectedDevice = _devices?.find(
    (d) => d.handle === connectedDeviceHandle && (!d.initialized || (d.initialized && d.attached))
  )

  /**
   * Function to handle HID disconnect event
   * @param e The actual disconnect event.
   * Please note this does not trigger on clicking "disconnect" in the Device page, it triggers on an actual unplug of the device.
   */
  const onDisconnect = (e: HIDConnectionEvent) => {
    const device = _devices?.find((d) => d.handle === e.device)
    if (device !== undefined) {
      _connectedDeviceHandle = undefined
      _updateConnectedDevice(undefined)
    }
    setTriggerDisconnectExport(true)
    _refresh()
  }

  useEffect(() => {
    const listener = () => {
      setDevices(_devices)
      setConnectedDeviceHandle(_connectedDeviceHandle)
    }

    _listeners.push(listener)

    return () => {
      const index = _listeners.indexOf(listener)
      if (index === -1) {
        return
      }

      _listeners.splice(index, 1)
    }
  }, [])

  const deviceSerial = connectedDevice?.initialized ? connectedDevice.mcuSerialNum : null
  const deviceFWVersion = connectedDevice?.initialized ? connectedDevice.fwVersion.join('.') : null
  const deviceName = connectedDevice?.initialized ? connectedDevice.handle.productName : null
  useEffect(() => {
    if (connectedDevice?.initialized) {
      Sentry.setTag(FIRMWARE_VERSION_TAG, deviceFWVersion)
      Sentry.setTag(DEVICE_NAME_TAG, deviceName)
    } else {
      Sentry.setTag(FIRMWARE_VERSION_TAG, null)
      Sentry.setTag(DEVICE_NAME_TAG, null)
    }
  }, [connectedDevice?.initialized, deviceSerial, deviceFWVersion, deviceName])

  const _updateKeyPair = useCallback((keypair: AccountKeyPair) => {
    let updated = false
    _keypair = keypair // update global
    // Do auth handshake for any devices waiting for a keypair, whether connected or not.
    const newDevices = _devices?.map((device: Device) => {
      if (!device.initialized || device.handshaker !== undefined) {
        // skip devices that have already performed auth handshake
        return device
      }
      updated = true
      const keepAliveTimer =
        device.keepAliveTimer || new KeepAliveTimer(device.handle, _triggerHandshake)
      const handshaker = new Handshaker(_keypair!, keepAliveTimer, _forceRenderDevices)
      handshaker.run(device.handle) // spawn async
      const modifiedDevice = {
        ...device,
        handshaker,
        keepAliveTimer
      }
      if (device.handle === _connectedDeviceHandle) {
        _updateConnectedDevice(modifiedDevice)
      }
      return modifiedDevice
    })
    if (updated) {
      _devices = newDevices
      _notify()
    }
  }, [])

  return {
    devices,
    connectedDevice,
    connect: _connect,
    disconnect: _disconnect,
    refresh: _refresh,
    pairDevice: _pairDevice,
    onDisconnect,
    triggerDisconnectExport,
    updateKeyPair: _updateKeyPair
  }
}
